import React, { useRef, useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useCanvasStore, Item } from '@/stores/canvasStore';
import { usePartnrStore } from '@/stores/partnrStore';
import { usePartnrBetaStore } from '@/stores/partnrBetaStore';
import { useDragToBoardStore } from '@/stores/dragToBoardStore';
import { useHistoryStore } from '@/stores/historyStore';
import { useArrowStore, ConnectorPosition } from '@/stores/arrowStore';
import { StickyNote } from '@/components/items/StickyNote';
import { TextCard } from '@/components/items/TextCard';
import { DocumentCard } from '@/components/items/DocumentCard';
import { ImageCard } from '@/components/items/ImageCard';
import { FileCard } from '@/components/items/FileCard';
import { PdfCard } from '@/components/items/PdfCard';
import { BoardCard } from '@/components/items/BoardCard';
import { LinkCard } from '@/components/items/LinkCard';
import { TweetPreviewCard, isTweetItem } from '@/components/items/TweetPreviewCard';
import { VideoCard } from '@/components/items/VideoCard';
import { SyncedItemCard } from '@/components/items/SyncedItemCard';
import { ScrapedCreativeCard } from '@/components/items/ScrapedCreativeCard';
import { PartnrReferencedItemBorder } from '@/components/partnr/PartnrReferencedItemBorder';
import { BoardProfilePopover } from '@/components/partnr/BoardProfilePopover';
import { ConnectorPoints } from './arrows';
import { DragHandlePill } from './DragHandlePill';
import { 
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuShortcut,
  ContextMenuTrigger,
} from '@/components/ui/context-menu';
import { Trash2, Copy, Palette, Pencil, FolderOpen, Share2, Circle, Sparkles, Brain, ArrowRight, EyeOff, Eye } from 'lucide-react';
import { FOLDER_COLORS, getFolderColorFromValue } from '@/lib/folderColors';
import { motion } from 'framer-motion';
import { copyShareLink } from '@/lib/sharing';
import { Input } from '@/components/ui/input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';

interface CanvasItemProps {
  item: Item;
  zoom: number;
  isPanMode?: boolean; // FIX 4: Track if pan mode is active
  lodTier?: 1 | 2 | 3; // LOD tier: 1=full, 2=simplified, 3=dot
}

// Check if an item was recently created (for animation)
const isRecentlyCreated = (createdAt: string): boolean => {
  return Date.now() - new Date(createdAt).getTime() < 1000;
};

import { MIN_ITEM_X, MIN_ITEM_Y } from '@/lib/canvasConstants';

const CLICK_THRESHOLD = 5; // pixels - if mouse moves less than this, it's a click
const DOUBLE_CLICK_DELAY = 300; // ms - max time between clicks for double-click
// Allow items slightly into UI zones (half their size may peek under toolbar,
// but they remain grabbable). Hard-clamp only prevents items from going fully
// off-screen or completely behind overlays.
const MIN_DRAG_X = 0;
const MIN_DRAG_Y = 0;

// Spring animation constants for drop settle effect
const SPRING_TENSION = 350;
const SPRING_FRICTION = 26;
const SPRING_VELOCITY_FACTOR = 0.08; // How much release velocity affects overshoot

// LOD color map for dot/chip tier — type → color
const LOD_TYPE_COLORS: Record<string, string> = {
  sticky: '#FEF3C7',
  text_card: '#DBEAFE',
  document: '#E0E7FF',
  image: '#FCE7F3',
  video: '#FEE2E2',
  file: '#E5E7EB',
  link: '#D1FAE5',
  board_link: '#EDE9FE',
  synced_item: '#CFFAFE',
};

export function CanvasItem({ item, zoom, isPanMode = false, lodTier = 1 }: CanvasItemProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeEdge, setResizeEdge] = useState<string | null>(null); // 'n','s','e','w','ne','nw','se','sw'
  const [dragStart, setDragStart] = useState({ x: 0, y: 0, mouseX: 0, mouseY: 0 });
  const [resizeStart, setResizeStart] = useState({ w: 0, h: 0, x: 0, y: 0, posX: 0, posY: 0 });
  const lastClickTimeRef = useRef<number>(0);
  
  // Spring animation state for drop settle effect
  const [isSettling, setIsSettling] = useState(false);
  const [settleOffset, setSettleOffset] = useState({ x: 0, y: 0 });
  const settleAnimationRef = useRef<number | null>(null);
  const lastMousePosRef = useRef({ x: 0, y: 0 });
  const lastMouseTimeRef = useRef(0);
  const releaseVelocityRef = useRef({ x: 0, y: 0 });
  
  // Track original positions for undo
  const [originalPositions, setOriginalPositions] = useState<Map<string, { x: number; y: number }>>(new Map());
  
  // Fine-grained selectors: only re-render when relevant values change
  const isSelected = useCanvasStore((s) => s.selectedItemIds.has(item.id));
  const selectedItemIds = useCanvasStore((s) => s.selectedItemIds);
  const isEditingItem = useCanvasStore((s) => s.isEditingItem);
  const isAutoOrganizing = useCanvasStore((s) => s.isAutoOrganizing);
  const isClusteringEnabled = useCanvasStore((s) => s.isClusteringEnabled);
  const spatialClusters = useCanvasStore((s) => s.spatialClusters);
  const boards = useCanvasStore((s) => s.boards);

  // Actions are stable references — safe to destructure
  const {
    selectItem,
    updateItem,
    deleteItem,
    deleteItems,
    createItem,
    moveItemsToBoard,
    navigateToBoard,
    updateBoard,
    deleteBoard,
  } = useCanvasStore.getState();

  // Note: items accessed via useCanvasStore.getState().items in event handlers
  // to avoid subscription-based re-renders when items change

  // Dynamic min-height for text cards so users can't resize under the content
  const minTextCardHeight = useCanvasStore((s) => s.itemMinHeights[item.id] ?? 0);

  const { activeChats, isPartnrMode, partnrSelectedItemIds, togglePartnrItemSelection, canvasActionLoading, canvasActionSourceIds, canvasActionConnection } = usePartnrStore();
  const { pushAction } = useHistoryStore();
  const { isArrowMode, startDrawing } = useArrowStore();
  
  const dragToBoardStore = useDragToBoardStore();
  
  // Handler for when a connector point is clicked to start drawing an arrow
  const handleConnectorMouseDown = useCallback((position: ConnectorPosition, point: { x: number; y: number }) => {
    // Convert from relative item coords to absolute canvas coords
    const absoluteX = item.position_x + point.x;
    const absoluteY = item.position_y + point.y;
    startDrawing(absoluteX, absoluteY, item.id, position);
  }, [item.id, item.position_x, item.position_y, startDrawing]);
  
  const isBoardCard = item.item_type === 'board_link';
  const isDocument = item.item_type === 'document';
  const isTextEditable = item.item_type === 'sticky' || item.item_type === 'text_card';
  const isMediaItem = item.item_type === 'image' || item.item_type === 'video';
  const isReferencedByPartnr = activeChats.some(chat => chat.referenced_item_ids.includes(item.id));
  const isPartnrSelected = partnrSelectedItemIds.has(item.id);
  const partnrSelectionIndex = isPartnrSelected ? Array.from(partnrSelectedItemIds).indexOf(item.id) + 1 : 0;
  const isBeingProcessed = canvasActionLoading && canvasActionSourceIds.includes(item.id);
  const isPartnrResult = canvasActionConnection?.resultItemId === item.id;
  
  // Check if this is a newly created item (for scale-in animation)
  const isNewItemRef = useRef(isRecentlyCreated(item.created_at));
  const isNewItem = isNewItemRef.current;

  // State for board renaming and delete confirmation
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  // State for media item renaming
  const [isRenamingMedia, setIsRenamingMedia] = useState(false);
  const [mediaRenameValue, setMediaRenameValue] = useState('');
  
  // State for board profile popover
  const [showBoardProfile, setShowBoardProfile] = useState(false);
  const [boardProfilePos, setBoardProfilePos] = useState({ x: 0, y: 0 });
  
  // Partnr beta state
  const { isPartnrBetaEnabled } = usePartnrBetaStore();

  // Multi-item drag: calculate offsets for all selected items
  const [multiDragOffsets, setMultiDragOffsets] = useState<Map<string, { x: number; y: number }>>(new Map());

  // Get the linked board id for this board card
  const linkedBoardId = isBoardCard ? (item.content as { linked_board_id?: string }).linked_board_id : null;
  const linkedBoardName = isBoardCard ? (item.content as { name?: string }).name : null;

  // Check if this board card is being hovered during drag
  const isDropTarget = dragToBoardStore.isDragging && 
                       dragToBoardStore.hoverBoardId === linkedBoardId && 
                       isBoardCard;

  // Progress timer for drag-to-board hover feedback.
  // On hold complete we auto-open the board for "drag-through" behavior, but
  // we DO NOT move items here. Movement is finalized on mouseup in
  // InfiniteCanvas drop detection.
  useEffect(() => {
    if (!isDropTarget) {
      return;
    }

    const { PHASE_TIMINGS } = useDragToBoardStore.getState();

    const interval = setInterval(() => {
      const dragState = useDragToBoardStore.getState();
      if (!dragState.hoverStartTime) return;

      const elapsed = Date.now() - dragState.hoverStartTime;

      if (elapsed < PHASE_TIMINGS.HOVER_TO_HOLD) {
        dragState.setProgress(0, 'hovering');
        return;
      }

      if (elapsed < PHASE_TIMINGS.HOVER_TO_HOLD + PHASE_TIMINGS.HOLD_TO_OPEN) {
        const holdElapsed = elapsed - PHASE_TIMINGS.HOVER_TO_HOLD;
        const progress = (holdElapsed / PHASE_TIMINGS.HOLD_TO_OPEN) * 100;
        dragState.setProgress(progress, 'holding');
        return;
      }

      // Hold threshold reached: show "opening" feedback and open target board.
      // Keep drag state active so mouseup can finalize the move.
      dragState.setOpening();
      clearInterval(interval);

      if (!linkedBoardId) return;
      const targetBoard = boards.find(b => b.id === linkedBoardId);
      if (!targetBoard) return;

      navigateToBoard(targetBoard);
    }, 30);

    return () => clearInterval(interval);
  }, [isDropTarget, linkedBoardId, boards, navigateToBoard]);

  // Handle mouse enter/leave for board cards during drag AND cluster highlighting
  const handleMouseEnter = useCallback(() => {
    // Cluster highlighting on any item hover
    if (isClusteringEnabled && spatialClusters.length > 0) {
      const cluster = spatialClusters.find(c => c.memberItemIds.includes(item.id));
      if (cluster) {
        useCanvasStore.setState({ highlightedClusterId: cluster.id });
      }
    }
    
    // Board card drag-to-board logic
    if (!isBoardCard || !linkedBoardId) return;

    const dragState = useDragToBoardStore.getState();
    if (!dragState.isDragging) return;

    // Don't allow dropping onto self
    if (dragState.draggedItemIds.includes(item.id)) return;

    dragState.enterBoard(linkedBoardId);
  }, [isBoardCard, linkedBoardId, item.id, isClusteringEnabled, spatialClusters]);

  const handleMouseLeave = useCallback(() => {
    // Clear cluster highlighting
    useCanvasStore.setState({ highlightedClusterId: null });
    
    // Board card drag-to-board logic
    if (!isBoardCard) return;

    const dragState = useDragToBoardStore.getState();
    if (!dragState.isDragging) return;

    dragState.leaveBoard();
  }, [isBoardCard]);

  // Check if we're in a drag zone (for text-editable items)
  const isInDragZone = useCallback((e: React.MouseEvent): boolean => {
    if (!isTextEditable) return true; // Non-text items are always draggable

    const target = e.target as HTMLElement;

    // Always allow drag from the DragHandlePill
    if (target.closest('[data-drag-handle]')) return true;

    // Check if the item has data-drag-zone="true"
    const dragZoneElement = target.closest('[data-drag-zone]');
    if (dragZoneElement) {
      return dragZoneElement.getAttribute('data-drag-zone') === 'true';
    }

    // Check if clicking on a resize edge (don't treat as drag)
    if (target.closest('[data-resize-edge]')) return false;

    // Fallback: check position within the item
    const itemElement = ref.current;
    if (!itemElement) return true;

    const rect = itemElement.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const heightPercent = y / rect.height;

    // Top/bottom 20% are drag zones
    return heightPercent < 0.2 || heightPercent > 0.8;
  }, [isTextEditable]);

  // Standard drag behavior: mousedown starts drag, mousemove moves item, mouseup ends drag
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return;
    
    // FIX 4: Don't allow any interaction in pan mode
    if (isPanMode) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    
    // Don't start drag if we're editing text
    if (isEditingItem) return;

    // Check if clicking resize handle or edge zone
    const target = e.target as HTMLElement;
    const resizeTarget = target.closest('[data-resize-edge]') as HTMLElement | null;
    const edge = target.dataset.resize || target.dataset.resizeEdge || resizeTarget?.dataset.resizeEdge;
    if (edge) {
      e.stopPropagation();
      e.preventDefault();
      setIsResizing(true);
      setResizeEdge(edge);
      setResizeStart({
        w: item.width,
        h: item.height,
        x: e.clientX,
        y: e.clientY,
        posX: item.position_x,
        posY: item.position_y,
      });
      return;
    }

    // Don't start drag if we're editing text
    if (isEditingItem) return;
    
    // For text-editable items, only allow dragging from drag zones
    if (isTextEditable && !isInDragZone(e)) {
      // Don't stop propagation - let the click reach the text component for editing
      return;
    }
    
    // For document items, use similar drag zone logic as text-editable items
    // Top 20% and bottom 20% are drag zones, middle 60% is for clicking to open editor
    if (isDocument) {
      const rect = ref.current?.getBoundingClientRect();
      if (rect) {
        const y = e.clientY - rect.top;
        const heightPercent = y / rect.height;
        // Only drag from top/bottom 20% edges (same as text-editable items)
        if (heightPercent > 0.2 && heightPercent < 0.8) {
          // Middle zone: let the click pass through to DocumentCard for opening editor
          return;
        }
      }
    }
    
    // For link items, let clicks pass through to allow opening links
    // Only drag from edges (top/bottom 15%)
    if (item.item_type === 'link') {
      const rect = ref.current?.getBoundingClientRect();
      if (rect) {
        const y = e.clientY - rect.top;
        const heightPercent = y / rect.height;
        // Allow clicks in main content area (85% of card)
        if (heightPercent > 0.15 && heightPercent < 0.85) {
          return;
        }
      }
    }
    
    // CRITICAL: Stop propagation to prevent canvas click handler from deselecting
    e.stopPropagation();
    e.preventDefault(); // Prevent text selection during drag
    
    // Cancel any ongoing spring animation when starting new drag
    if (settleAnimationRef.current) {
      cancelAnimationFrame(settleAnimationRef.current);
      settleAnimationRef.current = null;
      setIsSettling(false);
      setSettleOffset({ x: 0, y: 0 });
    }

    // Start tracking for drag
    setIsDragging(true);
    setDragStart({
      x: e.clientX / zoom - item.position_x,
      y: e.clientY / zoom - item.position_y,
      mouseX: e.clientX,
      mouseY: e.clientY,
    });
    
    // Initialize velocity tracking
    lastMousePosRef.current = { x: e.clientX, y: e.clientY };
    lastMouseTimeRef.current = performance.now();
    releaseVelocityRef.current = { x: 0, y: 0 };
    
    // Select item immediately when mouse goes down (before any drag)
    // This ensures selection is visible right away
    if (!isPartnrMode && !isSelected && !e.shiftKey) {
      selectItem(item.id, false);
    }

    // Calculate offsets for all selected items for multi-drag and store original positions
    // Need to check AFTER we potentially selected the item above
    const currentSelection = isSelected ? selectedItemIds : new Set([item.id]);
    if (!isPartnrMode) {
      const offsets = new Map<string, { x: number; y: number }>();
      const positions = new Map<string, { x: number; y: number }>();
      
      currentSelection.forEach(itemId => {
        const currentItems = useCanvasStore.getState().items;
        const targetItem = currentItems.find(i => i.id === itemId);
        if (targetItem) {
          offsets.set(itemId, {
            x: e.clientX / zoom - targetItem.position_x,
            y: e.clientY / zoom - targetItem.position_y,
          });
          positions.set(itemId, {
            x: targetItem.position_x,
            y: targetItem.position_y,
          });
        }
      });
      setMultiDragOffsets(offsets);
      setOriginalPositions(positions);
    } else {
      // Single item drag in Partnr mode
      setOriginalPositions(new Map([[item.id, { x: item.position_x, y: item.position_y }]]));
    }

    // Start drag-to-board tracking (for all items including board cards)
    if (!isPartnrMode) {
      const itemIds = isSelected && selectedItemIds.size > 0
        ? Array.from(selectedItemIds)
        : [item.id];
      dragToBoardStore.startDrag(itemIds);
    }

  }, [item, isSelected, selectItem, zoom, isEditingItem, isPartnrMode, selectedItemIds, isBoardCard, isTextEditable, isInDragZone, isPanMode]);

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (isDragging) {
      // Track velocity for spring animation on release
      const now = performance.now();
      const deltaTime = now - lastMouseTimeRef.current;
      if (deltaTime > 0) {
        releaseVelocityRef.current = {
          x: (e.clientX - lastMousePosRef.current.x) / deltaTime,
          y: (e.clientY - lastMousePosRef.current.y) / deltaTime,
        };
      }
      lastMousePosRef.current = { x: e.clientX, y: e.clientY };
      lastMouseTimeRef.current = now;
      
      // Multi-item drag: move all selected items
      if (!isPartnrMode && selectedItemIds.size > 0 && selectedItemIds.has(item.id)) {
        multiDragOffsets.forEach((offset, itemId) => {
          const newX = e.clientX / zoom - offset.x;
          const newY = e.clientY / zoom - offset.y;
          
          // Snap to grid and constrain to stay within visible bounds
          const snappedX = Math.round(newX / 20) * 20;
          const snappedY = Math.round(newY / 20) * 20;
          const constrainedX = Math.max(MIN_DRAG_X, snappedX);
          const constrainedY = Math.max(MIN_DRAG_Y, snappedY);
          
          updateItem(itemId, {
            position_x: constrainedX,
            position_y: constrainedY,
          });
        });
      } else {
        // Single item drag
        const newX = e.clientX / zoom - dragStart.x;
        const newY = e.clientY / zoom - dragStart.y;
        
        // Snap to grid and constrain to stay within visible bounds
        const snappedX = Math.round(newX / 20) * 20;
        const snappedY = Math.round(newY / 20) * 20;
        const constrainedX = Math.max(MIN_DRAG_X, snappedX);
        const constrainedY = Math.max(MIN_DRAG_Y, snappedY);
        
        updateItem(item.id, {
          position_x: constrainedX,
          position_y: constrainedY,
        });
      }
    }
    
    if (isResizing && resizeEdge) {
      const deltaX = (e.clientX - resizeStart.x) / zoom;
      const deltaY = (e.clientY - resizeStart.y) / zoom;
      // Minimum sizes - larger for content cards, smaller for others
      const isContentCard = item.item_type === 'sticky' || item.item_type === 'text_card';
      const minWidth = isContentCard ? 100 : 60;
      const baseMinHeight = isContentCard ? 80 : 40;
      // For text cards, use the dynamic min-height from content measurement
      const minHeight = item.item_type === 'text_card'
        ? Math.max(baseMinHeight, minTextCardHeight)
        : baseMinHeight;

      // Calculate new dimensions based on which edge/corner is being dragged
      let newWidth = resizeStart.w;
      let newHeight = resizeStart.h;
      let newPosX = resizeStart.posX;
      let newPosY = resizeStart.posY;

      const edge = resizeEdge;

      // Determine which axes are involved
      const resizesRight = edge.includes('e') || edge === 'true';
      const resizesLeft = edge.includes('w');
      const resizesDown = edge.includes('s') || edge === 'true';
      const resizesUp = edge.includes('n');

      if (resizesRight) {
        newWidth = resizeStart.w + deltaX;
      }
      if (resizesLeft) {
        newWidth = resizeStart.w - deltaX;
        newPosX = resizeStart.posX + deltaX;
      }
      if (resizesDown) {
        newHeight = resizeStart.h + deltaY;
      }
      if (resizesUp) {
        newHeight = resizeStart.h - deltaY;
        newPosY = resizeStart.posY + deltaY;
      }

      // Handle aspect ratio locking for images
      if (item.item_type === 'image' && !e.shiftKey) {
        const content = item.content as { aspectRatio?: number };
        const aspectRatio = content.aspectRatio || (resizeStart.w / resizeStart.h);
        newHeight = newWidth / aspectRatio;
      }

      // Lock aspect ratio for board_link (folders) - always 4:3 (200:150)
      if (item.item_type === 'board_link') {
        const folderAspectRatio = 200 / 150; // 4:3
        newHeight = newWidth / folderAspectRatio;
      }

      // Clamp to minimums
      if (newWidth < minWidth) {
        if (edge.includes('w')) {
          newPosX = resizeStart.posX + resizeStart.w - minWidth;
        }
        newWidth = minWidth;
      }
      if (newHeight < minHeight) {
        if (edge.includes('n')) {
          newPosY = resizeStart.posY + resizeStart.h - minHeight;
        }
        newHeight = minHeight;
      }

      // Snap to grid
      const snappedWidth = Math.max(minWidth, Math.round(newWidth / 20) * 20);
      const snappedHeight = Math.max(minHeight, Math.round(newHeight / 20) * 20);
      const snappedPosX = Math.round(newPosX / 20) * 20;
      const snappedPosY = Math.round(newPosY / 20) * 20;

      const updates: Record<string, number> = {
        width: snappedWidth,
        height: snappedHeight,
      };

      // Only update position if resizing from left/top edges
      if (resizesLeft) {
        updates.position_x = Math.max(MIN_DRAG_X, snappedPosX);
      }
      if (resizesUp) {
        updates.position_y = Math.max(MIN_DRAG_Y, snappedPosY);
      }

      updateItem(item.id, updates);
    }
  }, [isDragging, isResizing, resizeEdge, item.id, dragStart, resizeStart, zoom, updateItem, isPartnrMode, selectedItemIds, multiDragOffsets, minTextCardHeight]);

  // Spring animation for drop settle effect.
  // Animates a small position offset back to zero. Clamped to a safe maximum
  // so a high-velocity fling can never push the visible item far off-screen.
  // (The previous implementation also animated a separate scale spring with
  // an effective stiffness of 17500 and friction of 26 — wildly under-damped
  // for explicit Euler — which diverged to negative/NaN scales within a few
  // frames and made items visibly disappear right after release.)
  const startSpringAnimation = useCallback(() => {
    // Cancel any existing animation
    if (settleAnimationRef.current) {
      cancelAnimationFrame(settleAnimationRef.current);
    }

    // Calculate initial overshoot based on release velocity, capped so a fast
    // fling can't translate the item by hundreds of pixels.
    const velocity = releaseVelocityRef.current;
    const MAX_OVERSHOOT = 12; // pixels
    const rawOffsetX = velocity.x * SPRING_VELOCITY_FACTOR * 20;
    const rawOffsetY = velocity.y * SPRING_VELOCITY_FACTOR * 20;
    const initialOffsetX = Math.max(-MAX_OVERSHOOT, Math.min(MAX_OVERSHOOT, rawOffsetX));
    const initialOffsetY = Math.max(-MAX_OVERSHOOT, Math.min(MAX_OVERSHOOT, rawOffsetY));

    // Only animate if there was meaningful movement
    const speed = Math.sqrt(velocity.x * velocity.x + velocity.y * velocity.y);
    if (speed < 0.1) {
      return;
    }

    setIsSettling(true);
    setSettleOffset({ x: initialOffsetX, y: initialOffsetY });

    // Semi-implicit Euler spring with real frame time. Stable for the chosen
    // tension/friction so the item always settles back to (0, 0).
    let offsetX = initialOffsetX;
    let offsetY = initialOffsetY;
    let velX = 0;
    let velY = 0;
    let lastTime = performance.now();

    const animate = (now: number) => {
      // Clamp dt so a long stall (tab background, breakpoint) can't blow up
      // the integrator on resume.
      const dt = Math.min(0.032, (now - lastTime) / 1000);
      lastTime = now;

      const forceX = -SPRING_TENSION * offsetX - SPRING_FRICTION * velX;
      const forceY = -SPRING_TENSION * offsetY - SPRING_FRICTION * velY;

      velX += forceX * dt;
      velY += forceY * dt;
      offsetX += velX * dt;
      offsetY += velY * dt;

      // Defensive: if anything goes non-finite, snap to rest immediately.
      if (!Number.isFinite(offsetX) || !Number.isFinite(offsetY)) {
        setIsSettling(false);
        setSettleOffset({ x: 0, y: 0 });
        settleAnimationRef.current = null;
        return;
      }

      setSettleOffset({ x: offsetX, y: offsetY });

      const isAtRest =
        Math.abs(offsetX) < 0.1 &&
        Math.abs(offsetY) < 0.1 &&
        Math.abs(velX) < 0.5 &&
        Math.abs(velY) < 0.5;

      if (isAtRest) {
        setIsSettling(false);
        setSettleOffset({ x: 0, y: 0 });
        settleAnimationRef.current = null;
      } else {
        settleAnimationRef.current = requestAnimationFrame(animate);
      }
    };

    settleAnimationRef.current = requestAnimationFrame(animate);
  }, []);

  const handleMouseUp = useCallback((e: MouseEvent) => {
    // End drag-to-board tracking first
    if (!isPartnrMode) {
      dragToBoardStore.endDrag();
    }
    
    if (isDragging) {
      // Calculate how far the mouse moved
      const moveDistance = Math.sqrt(
        Math.pow(e.clientX - dragStart.mouseX, 2) +
        Math.pow(e.clientY - dragStart.mouseY, 2)
      );

      // If mouse barely moved, this was a click - handle selection and double-click
      const effectiveThreshold = CLICK_THRESHOLD;

      if (moveDistance < effectiveThreshold) {
        const now = Date.now();
        const timeSinceLastClick = now - lastClickTimeRef.current;

        // Board cards: double-click to navigate, single-click to select (allows dragging)
        if (isBoardCard && linkedBoardId && !e.shiftKey && !isPartnrMode) {
          if (timeSinceLastClick < DOUBLE_CLICK_DELAY) {
            // Double-click — navigate into the board
            const targetBoard = boards.find(b => b.id === linkedBoardId);
            if (targetBoard) {
              navigateToBoard(targetBoard);
            }
            lastClickTimeRef.current = 0;
          } else {
            // Single click — just select (item was already selected in mouseDown)
            lastClickTimeRef.current = now;
          }
        } else {
          // Non-board items: single click handles selection
          lastClickTimeRef.current = now;

          if (isPartnrMode && !isBoardCard) {
            togglePartnrItemSelection(item.id);
          } else if (e.shiftKey) {
            // Shift+click toggles selection (additive mode)
            selectItem(item.id, true);
          }
          // For regular clicks, item was already selected in mouseDown
          // No need to select again - it would just re-select the same item
        }
      } else {
        // This was a real drag - start spring animation for settle effect
        startSpringAnimation();
        
        // Record for undo
        const newPositions = new Map<string, { x: number; y: number }>();
        const itemsToTrack = selectedItemIds.size > 0 && selectedItemIds.has(item.id) 
          ? [...selectedItemIds] 
          : [item.id];
        
        const currentItems = useCanvasStore.getState().items;
        itemsToTrack.forEach(itemId => {
          const targetItem = currentItems.find(i => i.id === itemId);
          if (targetItem) {
            newPositions.set(itemId, {
              x: targetItem.position_x,
              y: targetItem.position_y,
            });
          }
        });
        
        // Only push to history if positions actually changed
        if (originalPositions.size > 0) {
          let positionsChanged = false;
          for (const [id, pos] of originalPositions) {
            const newPos = newPositions.get(id);
            if (newPos && (newPos.x !== pos.x || newPos.y !== pos.y)) {
              positionsChanged = true;
              break;
            }
          }
          
          if (positionsChanged) {
            pushAction({
              type: itemsToTrack.length > 1 ? 'MOVE_ITEMS' : 'MOVE_ITEM',
              timestamp: Date.now(),
              previousPositions: originalPositions,
              newPositions,
            });
          }
        }

        // Check if item was dropped in/out of a cluster
        const storeState = useCanvasStore.getState();
        if (storeState.isClusteringEnabled && storeState.spatialClusters.length > 0) {
          const currentItem = storeState.items.find(i => i.id === item.id);
          if (currentItem) {
            const itemCenterX = currentItem.position_x + currentItem.width / 2;
            const itemCenterY = currentItem.position_y + currentItem.height / 2;
            const currentCluster = storeState.spatialClusters.find(c => c.memberItemIds.includes(item.id));

            // Find cluster whose live bounding box contains this item's center
            const targetCluster = storeState.spatialClusters.find(c => {
              if (c.id === currentCluster?.id) return false;
              const members = storeState.items.filter(i => c.memberItemIds.includes(i.id));
              if (members.length === 0) return false;
              let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
              for (const m of members) {
                minX = Math.min(minX, m.position_x);
                minY = Math.min(minY, m.position_y);
                maxX = Math.max(maxX, m.position_x + m.width);
                maxY = Math.max(maxY, m.position_y + m.height);
              }
              const pad = 24;
              return itemCenterX >= minX - pad && itemCenterX <= maxX + pad &&
                     itemCenterY >= minY - pad && itemCenterY <= maxY + pad;
            });

            if (targetCluster) {
              storeState.moveItemToCluster(item.id, targetCluster.id);
            } else if (currentCluster) {
              // Check if item was dragged far enough OUT of its current cluster
              const others = storeState.items.filter(i => currentCluster.memberItemIds.includes(i.id) && i.id !== item.id);
              if (others.length > 0) {
                let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
                for (const m of others) {
                  minX = Math.min(minX, m.position_x);
                  minY = Math.min(minY, m.position_y);
                  maxX = Math.max(maxX, m.position_x + m.width);
                  maxY = Math.max(maxY, m.position_y + m.height);
                }
                const pad = 60;
                const outsideCluster = itemCenterX < minX - pad || itemCenterX > maxX + pad ||
                                       itemCenterY < minY - pad || itemCenterY > maxY + pad;
                if (outsideCluster) {
                  storeState.moveItemToCluster(item.id, null);
                }
              }
            }
          }
        }
      }
    }

    setIsDragging(false);
    setIsResizing(false);
    setResizeEdge(null);
    setMultiDragOffsets(new Map());
    setOriginalPositions(new Map());
    releaseVelocityRef.current = { x: 0, y: 0 };
  }, [isDragging, dragStart, selectItem, item.id, isPartnrMode, isBoardCard, togglePartnrItemSelection, selectedItemIds, originalPositions, pushAction, linkedBoardId, boards, navigateToBoard, dragToBoardStore, startSpringAnimation]);

  // Add/remove event listeners
  React.useEffect(() => {
    if (isDragging || isResizing) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, isResizing, handleMouseMove, handleMouseUp]);

  // ── Touch drag support ──
  // Mirrors mouse drag but using touch events.  The touchstart handler is
  // attached via onTouchStart on the DOM element; touchmove/touchend are
  // attached to window (like mousemove/mouseup) so drags continue even if
  // the finger leaves the element.

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (e.touches.length !== 1) return; // only single-finger drag
    if (isPanMode) return;
    if (isEditingItem) return;

    const touch = e.touches[0];

    // For text-editable items, only allow dragging from drag zones
    if (isTextEditable) {
      const rect = ref.current?.getBoundingClientRect();
      if (rect) {
        const y = touch.clientY - rect.top;
        const heightPercent = y / rect.height;
        if (heightPercent > 0.15 && heightPercent < 0.85) return;
      }
    }

    // For documents, same drag zone logic
    if (isDocument) {
      const rect = ref.current?.getBoundingClientRect();
      if (rect) {
        const y = touch.clientY - rect.top;
        const heightPercent = y / rect.height;
        if (heightPercent > 0.2 && heightPercent < 0.8) return;
      }
    }

    // For links, same edge-only drag
    if (item.item_type === 'link') {
      const rect = ref.current?.getBoundingClientRect();
      if (rect) {
        const y = touch.clientY - rect.top;
        const heightPercent = y / rect.height;
        if (heightPercent > 0.15 && heightPercent < 0.85) return;
      }
    }

    e.stopPropagation(); // prevent canvas from starting its own pan

    // Cancel spring animation
    if (settleAnimationRef.current) {
      cancelAnimationFrame(settleAnimationRef.current);
      settleAnimationRef.current = null;
      setIsSettling(false);
      setSettleOffset({ x: 0, y: 0 });
    }

    setIsDragging(true);
    setDragStart({
      x: touch.clientX / zoom - item.position_x,
      y: touch.clientY / zoom - item.position_y,
      mouseX: touch.clientX,
      mouseY: touch.clientY,
    });

    lastMousePosRef.current = { x: touch.clientX, y: touch.clientY };
    lastMouseTimeRef.current = performance.now();
    releaseVelocityRef.current = { x: 0, y: 0 };

    // Select item
    if (!isPartnrMode && !isSelected) {
      selectItem(item.id, false);
    }

    // Multi-drag offsets
    const currentSelection = isSelected ? selectedItemIds : new Set([item.id]);
    if (!isPartnrMode) {
      const offsets = new Map<string, { x: number; y: number }>();
      const positions = new Map<string, { x: number; y: number }>();
      currentSelection.forEach(itemId => {
        const currentItems = useCanvasStore.getState().items;
        const targetItem = currentItems.find(i => i.id === itemId);
        if (targetItem) {
          offsets.set(itemId, {
            x: touch.clientX / zoom - targetItem.position_x,
            y: touch.clientY / zoom - targetItem.position_y,
          });
          positions.set(itemId, { x: targetItem.position_x, y: targetItem.position_y });
        }
      });
      setMultiDragOffsets(offsets);
      setOriginalPositions(positions);
    } else {
      setOriginalPositions(new Map([[item.id, { x: item.position_x, y: item.position_y }]]));
    }

    if (!isPartnrMode) {
      const itemIds = isSelected && selectedItemIds.size > 0 ? Array.from(selectedItemIds) : [item.id];
      dragToBoardStore.startDrag(itemIds);
    }
  }, [item, isSelected, selectItem, zoom, isEditingItem, isPartnrMode, selectedItemIds, isBoardCard, isTextEditable, isDocument, isPanMode]);

  // Touch move/end listeners (window-level so drag continues outside element)
  React.useEffect(() => {
    if (!isDragging) return;

    const onTouchMove = (e: TouchEvent) => {
      if (e.touches.length !== 1) return;
      const touch = e.touches[0];

      // Velocity tracking
      const now = performance.now();
      const dt = now - lastMouseTimeRef.current;
      if (dt > 0) {
        releaseVelocityRef.current = {
          x: (touch.clientX - lastMousePosRef.current.x) / dt,
          y: (touch.clientY - lastMousePosRef.current.y) / dt,
        };
      }
      lastMousePosRef.current = { x: touch.clientX, y: touch.clientY };
      lastMouseTimeRef.current = now;

      // Reuse handleMouseMove logic — synthesize a minimal event-like object
      handleMouseMove({ clientX: touch.clientX, clientY: touch.clientY } as MouseEvent);
    };

    const onTouchEnd = (e: TouchEvent) => {
      const touch = e.changedTouches[0];
      // Reuse handleMouseUp with a synthetic event
      handleMouseUp({ clientX: touch.clientX, clientY: touch.clientY } as MouseEvent);
    };

    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend', onTouchEnd, { passive: false });
    window.addEventListener('touchcancel', onTouchEnd, { passive: false });
    return () => {
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
      window.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Cleanup spring animation on unmount
  React.useEffect(() => {
    return () => {
      if (settleAnimationRef.current) {
        cancelAnimationFrame(settleAnimationRef.current);
      }
    };
  }, []);

  const handleDuplicate = useCallback(async () => {
    const content = typeof item.content === 'object' && item.content !== null 
      ? item.content as Record<string, unknown>
      : {};
    await createItem({
      board_id: item.board_id,
      item_type: item.item_type,
      position_x: item.position_x + 20,
      position_y: item.position_y + 20,
      width: item.width,
      height: item.height,
      content,
    });
  }, [item, createItem]);

  // Board-specific handlers
  const handleOpenBoard = useCallback(() => {
    if (!linkedBoardId) return;
    const targetBoard = boards.find(b => b.id === linkedBoardId);
    if (targetBoard) {
      navigateToBoard(targetBoard);
    }
  }, [linkedBoardId, boards, navigateToBoard]);

  const handleRenameBoard = useCallback(() => {
    if (!linkedBoardId) return;
    setRenameValue(linkedBoardName || 'Untitled Board');
    setIsRenaming(true);
  }, [linkedBoardId, linkedBoardName]);

  const handleRenameSubmit = useCallback(async () => {
    if (!linkedBoardId || !renameValue.trim()) return;
    await updateBoard(linkedBoardId, { name: renameValue.trim() });
    setIsRenaming(false);
    toast.success('Board renamed');
  }, [linkedBoardId, renameValue, updateBoard]);

  const handleCopyShareLink = useCallback(async () => {
    const type = item.item_type === 'board_link' ? 'board' : 'item';
    const targetId = item.item_type === 'board_link' 
      ? (item.content as { linked_board_id: string }).linked_board_id 
      : item.id;
    
    await copyShareLink(type, targetId);
  }, [item]);

  // Media item rename handlers
  const handleRenameMedia = useCallback(() => {
    const content = item.content as { filename?: string; title?: string };
    setMediaRenameValue(content.title || content.filename || 'Untitled');
    setIsRenamingMedia(true);
  }, [item.content]);

  const handleMediaRenameSubmit = useCallback(async () => {
    if (!mediaRenameValue.trim()) return;
    const content = typeof item.content === 'object' && item.content !== null ? item.content : {};
    await updateItem(item.id, { 
      content: { ...content, title: mediaRenameValue.trim() } 
    });
    setIsRenamingMedia(false);
    toast.success('Item renamed');
  }, [item.id, mediaRenameValue, updateItem]);

  const handleCopyContent = useCallback(async () => {
    const content = item.content as Record<string, any>;
    let textToCopy = '';
    
    if (item.item_type === 'sticky') {
      textToCopy = content.text || '';
    } else if (item.item_type === 'text_card') {
      // Strip HTML tags to get plain text
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = content.body || '';
      const plainBody = tempDiv.textContent || tempDiv.innerText || '';
      textToCopy = content.title ? `${content.title}\n\n${plainBody}` : plainBody;
    } else if (item.item_type === 'image') {
      textToCopy = content.url || '';
    }
    
    if (textToCopy) {
      await navigator.clipboard.writeText(textToCopy);
      toast.success('Copied to clipboard');
    }
  }, [item]);

  const handleDeleteBoardConfirm = useCallback(async () => {
    if (!linkedBoardId) return;
    await deleteBoard(linkedBoardId);
    setShowDeleteConfirm(false);
  }, [linkedBoardId, deleteBoard]);

  // Handle height changes from auto-expanding cards
  const handleHeightChange = useCallback((newHeight: number) => {
    if (newHeight !== item.height) {
      updateItem(item.id, { height: newHeight });
    }
  }, [item.id, item.height, updateItem]);

  const renderContent = () => {
    switch (item.item_type) {
      case 'sticky':
        return <StickyNote item={item} onHeightChange={handleHeightChange} />;
      case 'text_card':
        return <TextCard item={item} onHeightChange={handleHeightChange} />;
      case 'document':
        return <DocumentCard item={item} />;
      case 'image':
        return <ImageCard item={item} />;
      case 'file': {
        const fileContent = item.content as { mimeType?: string };
        if (fileContent.mimeType === 'application/pdf') {
          return <PdfCard item={item} />;
        }
        return <FileCard item={item} />;
      }
      case 'board_link':
        return <BoardCard item={item} isSelected={isSelected} />;
      case 'link':
        if (isTweetItem(item)) return <TweetPreviewCard item={item} />;
        return <LinkCard item={item} />;
      case 'video':
        return <VideoCard item={item} />;
      case 'synced_item':
        return <SyncedItemCard item={item} />;
      case 'scraped_creative':
        return <ScrapedCreativeCard item={item} />;
      default:
        return null;
    }
  };

  // Determine cursor based on item type and state
  const getCursor = () => {
    // FIX 4: In pan mode, no special cursors
    if (isPanMode) return 'default';
    if (isDragging || isResizing) return 'grabbing';
    if (isBoardCard) return 'pointer';
    if (isTextEditable) return 'default'; // Let the child component handle cursor
    return 'grab';
  };

  // Determine if drag handle should be visible
  // Hide in arrow mode so the pill (z-20) doesn't block the top connector point
  const showDragHandle = isHovering && !isDragging && !isResizing && !isPanMode && !isPartnrMode && !isArrowMode;

  // --- Ghost Item Rendering (Phase 4 — streaming placeholders) ---
  if (item.isGhost) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ type: 'spring', stiffness: 300, damping: 25 }}
        className="absolute rounded-lg overflow-hidden"
        style={{
          left: item.position_x,
          top: item.position_y,
          width: item.width,
          height: item.height,
          zIndex: item.z_index,
        }}
      >
        <div
          className="w-full h-full rounded-lg border-2 border-dashed"
          style={item.ghostColor ? {
            borderColor: item.ghostColor + '80',
            backgroundColor: item.ghostColor + '15',
          } : {
            borderColor: 'hsl(var(--partnr) / 0.4)',
            backgroundColor: 'hsl(var(--partnr) / 0.05)',
          }}
        >
          {/* Shimmer animation overlay */}
          <div className="absolute inset-0 overflow-hidden rounded-lg">
            <div className="ghost-shimmer absolute inset-0" />
          </div>

          {/* Progress indicator */}
          <div
            className="absolute top-0 left-0 right-0 transition-all duration-300 ease-out rounded-t-lg"
            style={{
              height: `${(item.streamProgress ?? 0) * 100}%`,
              backgroundColor: item.ghostColor ? item.ghostColor + '20' : 'hsl(var(--partnr) / 0.1)',
            }}
          />

          {/* Pulsing dot indicator */}
          <div className="absolute bottom-3 right-3">
            <div className="w-2 h-2 rounded-full bg-partnr animate-pulse" />
          </div>
        </div>
      </motion.div>
    );
  }

  // --- LOD Tier 3: Dot/Chip — colored rectangle, no text ---
  if (lodTier === 3) {
    const bgColor = LOD_TYPE_COLORS[item.item_type] || '#E5E7EB';
    return (
      <div
        data-canvas-item="true"
        data-item-id={item.id}
        className={`absolute rounded-sm ${isSelected ? 'ring-2 ring-partnr/70' : ''}`}
        style={{
          left: item.position_x,
          top: item.position_y,
          width: item.width,
          height: item.height,
          zIndex: item.z_index,
          backgroundColor: bgColor,
          opacity: 0.85,
        }}
      />
    );
  }

  // --- LOD Tier 2: Simplified — title-only card ---
  if (lodTier === 2) {
    const content = item.content as Record<string, unknown>;
    const title = (content.title as string) || (content.text as string) || (content.filename as string) || item.item_type;
    const bgColor = LOD_TYPE_COLORS[item.item_type] || '#F3F4F6';
    return (
      <div
        data-canvas-item="true"
        data-item-id={item.id}
        className={`absolute rounded-lg border border-border/50 overflow-hidden ${
          isSelected ? 'ring-2 ring-partnr/70' : ''
        }`}
        style={{
          left: item.position_x,
          top: item.position_y,
          width: item.width,
          height: Math.min(item.height, 48),
          zIndex: item.z_index,
          backgroundColor: bgColor,
        }}
      >
        <div className="px-2 py-1 text-xs font-medium truncate text-foreground/80">
          {String(title).substring(0, 60)}
        </div>
      </div>
    );
  }

  // --- LOD Tier 1: Full render (default) ---
  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <div
          ref={ref}
          data-canvas-item="true"
          data-item-id={item.id}
          className={`absolute select-none rounded-lg group ${
            isNewItem ? 'canvas-item-enter' : ''
          } ${
            isSelected && !isBoardCard ? 'ring-2 ring-partnr/70' : ''
          } ${
            isPartnrSelected ? 'ring-2 ring-partnr' : ''
          } ${
            isPanMode ? 'pointer-events-none' : ''
          } ${
            isBeingProcessed ? 'ring-2 ring-partnr/40 partnr-processing' : ''
          } ${
            isPartnrResult ? 'partnr-result-enter' : ''
          }`}
          style={{
            left: item.position_x,
            top: item.position_y,
            width: item.width,
            height: item.height,
            zIndex: item.z_index + (isDragging ? 1000 : 0),
            opacity: dragToBoardStore.isDragging && dragToBoardStore.draggedItemIds.includes(item.id) ? 0.6 : 1,
            cursor: getCursor(),
            // Disable pointer events on dragged items so board cards underneath can receive hover events
            pointerEvents: isDragging || isPanMode ? 'none' : 'auto',
            // Spring settle animation transform
            transform: isSettling
              ? `translate(${settleOffset.x}px, ${settleOffset.y}px)`
              : undefined,
            // Smooth animation during auto-organize
            transition: isAutoOrganizing
              ? 'left 0.4s cubic-bezier(0.4, 0, 0.2, 1), top 0.4s cubic-bezier(0.4, 0, 0.2, 1), width 0.4s cubic-bezier(0.4, 0, 0.2, 1), height 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
              : undefined,
          }}
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
          onClick={(e) => {
            // Stop board-card clicks from reaching the canvas — prevents the
            // canvas onDoubleClick from firing when the 1st click is on a
            // board card (which navigates) and the 2nd lands on the new
            // board's empty canvas.
            if (isBoardCard && linkedBoardId && !isPartnrMode) {
              e.stopPropagation();
            }
          }}
          onDoubleClick={(e) => {
            // Board cards: navigate on double-click as fallback (e.g. if single-
            // click was treated as a micro-drag). Stop propagation so the
            // InfiniteCanvas handler doesn't create a sticky note.
            if (isBoardCard && linkedBoardId && !isPartnrMode) {
              e.stopPropagation();
              const targetBoard = boards.find(b => b.id === linkedBoardId);
              if (targetBoard) {
                navigateToBoard(targetBoard);
              }
            }
          }}
          onMouseEnter={() => { setIsHovering(true); handleMouseEnter(); }}
          onMouseLeave={() => { setIsHovering(false); handleMouseLeave(); }}
        >
          {/* Drag Handle Pill at top center */}
          <DragHandlePill 
            isVisible={showDragHandle} 
            position="top"
          />
          
          {renderContent()}
          
          {/* Arrow connector points overlay - z-30 to sit above drag handle (z-20) and content menus (z-10) */}
          {isArrowMode && (
            <svg
              className="absolute inset-0 overflow-visible pointer-events-none z-30"
              style={{ width: item.width, height: item.height }}
            >
              <ConnectorPoints
                itemId={item.id}
                x={0}
                y={0}
                width={item.width}
                height={item.height}
                zoom={zoom}
                onConnectorMouseDown={handleConnectorMouseDown}
              />
            </svg>
          )}
          
          {/* Partnr referenced item border */}
          {isReferencedByPartnr && <PartnrReferencedItemBorder item={item} />}
          
          {/* Partnr selection number badge */}
          {isPartnrSelected && partnrSelectionIndex > 0 && (
            <div className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-partnr text-partnr-foreground text-xs font-bold flex items-center justify-center shadow-md z-10">
              {partnrSelectionIndex}
            </div>
          )}
          
          {/* Partnr result styling handled by TextCard's dotted border */}
          
          {/* Edge/corner resize zones - visible when selected or hovering */}
          {(isSelected || isHovering) && !isPartnrMode && !isPanMode && !isDragging && (
            <>
              {/* Edge zones - invisible but interactive */}
              <div data-resize-edge="n" className="absolute -top-1 left-2 right-2 h-2 cursor-n-resize z-10" />
              <div data-resize-edge="s" className="absolute -bottom-1 left-2 right-2 h-2 cursor-s-resize z-10" />
              <div data-resize-edge="e" className="absolute top-2 -right-1 w-2 bottom-2 cursor-e-resize z-10" />
              <div data-resize-edge="w" className="absolute top-2 -left-1 w-2 bottom-2 cursor-w-resize z-10" />
              {/* Corner zones */}
              <div data-resize-edge="nw" className="absolute -top-1 -left-1 w-3 h-3 cursor-nw-resize z-20" />
              <div data-resize-edge="ne" className="absolute -top-1 -right-1 w-3 h-3 cursor-ne-resize z-20" />
              <div data-resize-edge="sw" className="absolute -bottom-1 -left-1 w-3 h-3 cursor-sw-resize z-20" />
              <div data-resize-edge="se" className="absolute -bottom-1 -right-1 w-3 h-3 cursor-se-resize z-20" />
              {/* Visual corner dots - only visible when selected */}
              {isSelected && (
                <>
                  <div className="absolute -top-1 -left-1 w-2.5 h-2.5 bg-primary rounded-full pointer-events-none z-20 opacity-80" />
                  <div className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-primary rounded-full pointer-events-none z-20 opacity-80" />
                  <div className="absolute -bottom-1 -left-1 w-2.5 h-2.5 bg-primary rounded-full pointer-events-none z-20 opacity-80" />
                  <div className="absolute -bottom-1 -right-1 w-2.5 h-2.5 bg-primary rounded-full pointer-events-none z-20 opacity-80" />
                </>
              )}
            </>
          )}
        </div>
      </ContextMenuTrigger>
      
      <ContextMenuContent className="w-52">
        {isSelected && selectedItemIds.size > 1 ? (
          <>
            {/* Multi-select menu */}
            <ContextMenuItem
              onClick={() => {
                const ids = Array.from(selectedItemIds);
                // Capture item snapshots for undo before deleting
                const currentItems = useCanvasStore.getState().items;
                const deletedItems = currentItems.filter(i => ids.includes(i.id));
                if (deletedItems.length > 0) {
                  pushAction({
                    type: 'DELETE_ITEMS',
                    timestamp: Date.now(),
                    items: deletedItems,
                  });
                }
                deleteItems(ids);
                toast.success(`Deleted ${ids.length} items`);
              }}
              className="text-destructive focus:bg-destructive focus:text-white"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete {selectedItemIds.size} items
            </ContextMenuItem>
          </>
        ) : (
          <>
            {/* Single-item context menu */}
            {/* Board-specific options */}
            {isBoardCard && (
              <>
                <ContextMenuItem onClick={handleOpenBoard}>
                  <FolderOpen className="mr-2 h-4 w-4" />
                  Open Board
                </ContextMenuItem>
                <ContextMenuItem onClick={handleRenameBoard}>
                  <Pencil className="mr-2 h-4 w-4" />
                  Rename
                </ContextMenuItem>
                {isPartnrBetaEnabled && linkedBoardId && (
                  <ContextMenuItem onClick={(e) => {
                    const rect = (e.target as HTMLElement).getBoundingClientRect();
                    setBoardProfilePos({ x: rect.right + 8, y: rect.top });
                    setShowBoardProfile(true);
                  }}>
                    <Brain className="mr-2 h-4 w-4 text-partnr" />
                    Board Profile
                  </ContextMenuItem>
                )}
                <ContextMenuSeparator />
                <div className="px-2 py-1.5 text-xs font-medium text-muted-foreground">Folder Color</div>
                <div className="grid grid-cols-5 gap-1 px-2 pb-2">
                  {FOLDER_COLORS.map((color) => {
                    const boardContent = item.content as { color?: string };
                    const currentColor = getFolderColorFromValue(boardContent.color);
                    const isCurrentColor = currentColor.id === color.id;
                    return (
                      <button
                        key={color.id}
                        onClick={() => {
                          const content = typeof item.content === 'object' && item.content !== null ? item.content : {};
                          updateItem(item.id, { content: { ...content, color: color.id } });
                          // Also update the board's icon_color
                          if (linkedBoardId) {
                            updateBoard(linkedBoardId, { icon_color: color.id });
                          }
                        }}
                        className={`w-6 h-6 rounded-full transition-all hover:scale-110 ${isCurrentColor ? 'ring-2 ring-foreground ring-offset-1' : ''}`}
                        style={{ backgroundColor: color.folder }}
                        title={color.name}
                      />
                    );
                  })}
                </div>
                <ContextMenuSeparator />
              </>
            )}
            
            {/* Non-board items can be duplicated */}
            {!isBoardCard && (
              <ContextMenuItem onClick={handleDuplicate}>
                <Copy className="mr-2 h-4 w-4" />
                Duplicate
              </ContextMenuItem>
            )}
            
            {/* Copy content - only for sticky, text_card, and image */}
            {(item.item_type === 'sticky' || item.item_type === 'text_card' || item.item_type === 'image') && (
              <ContextMenuItem onClick={handleCopyContent}>
                <Copy className="mr-2 h-4 w-4" />
                Copy content
                <ContextMenuShortcut>⌘C</ContextMenuShortcut>
              </ContextMenuItem>
            )}

            {/* Text card transparency toggle */}
            {item.item_type === 'text_card' && (
              <ContextMenuItem onClick={() => {
                const content = typeof item.content === 'object' && item.content !== null ? item.content : {};
                const isCurrentlyTransparent = !!(content as Record<string, unknown>).transparent;
                updateItem(item.id, { content: { ...content, transparent: !isCurrentlyTransparent } });
              }}>
                {(item.content as Record<string, unknown>)?.transparent ? (
                  <>
                    <Eye className="mr-2 h-4 w-4" />
                    Show background
                  </>
                ) : (
                  <>
                    <EyeOff className="mr-2 h-4 w-4" />
                    Transparent background
                  </>
                )}
              </ContextMenuItem>
            )}

            {/* Media items (image/video) can be renamed */}
            {isMediaItem && (
              <ContextMenuItem onClick={handleRenameMedia}>
                <Pencil className="mr-2 h-4 w-4" />
                Rename
              </ContextMenuItem>
            )}

            <ContextMenuItem onClick={handleCopyShareLink}>
              <Share2 className="mr-2 h-4 w-4" />
              Copy share link
            </ContextMenuItem>
            
            {item.item_type === 'sticky' && (
              <>
                <ContextMenuSeparator />
                <ContextMenuItem onClick={() => {
                  const content = typeof item.content === 'object' && item.content !== null ? item.content : {};
                  updateItem(item.id, { content: { ...content, color: 'yellow', customColor: undefined, colorCode: undefined } });
                }}>
                  <Palette className="mr-2 h-4 w-4 text-sticky-yellow" />
                  Yellow
                </ContextMenuItem>
                <ContextMenuItem onClick={() => {
                  const content = typeof item.content === 'object' && item.content !== null ? item.content : {};
                  updateItem(item.id, { content: { ...content, color: 'blue', customColor: undefined, colorCode: undefined } });
                }}>
                  <Palette className="mr-2 h-4 w-4 text-sticky-blue" />
                  Blue
                </ContextMenuItem>
                <ContextMenuItem onClick={() => {
                  const content = typeof item.content === 'object' && item.content !== null ? item.content : {};
                  updateItem(item.id, { content: { ...content, color: 'pink', customColor: undefined, colorCode: undefined } });
                }}>
                  <Palette className="mr-2 h-4 w-4 text-sticky-pink" />
                  Pink
                </ContextMenuItem>
                <ContextMenuItem onClick={() => {
                  const content = typeof item.content === 'object' && item.content !== null ? item.content : {};
                  updateItem(item.id, { content: { ...content, color: 'green', customColor: undefined, colorCode: undefined } });
                }}>
                  <Palette className="mr-2 h-4 w-4 text-sticky-green" />
                  Green
                </ContextMenuItem>
              </>
            )}
            <ContextMenuSeparator />
            <ContextMenuItem
              onClick={() => {
                // Enter arrow mode and start drawing from this item's right connector
                const arrowState = useArrowStore.getState();
                arrowState.setArrowMode(true);
                useCanvasStore.getState().setActiveTool('arrow');
                // Start drawing from right connector
                const rightPoint = {
                  x: item.position_x + item.width,
                  y: item.position_y + item.height / 2,
                };
                arrowState.startDrawing(rightPoint.x, rightPoint.y, item.id, 'right');
              }}
            >
              <ArrowRight className="mr-2 h-4 w-4" />
              Connect to...
            </ContextMenuItem>
            <ContextMenuItem
              onClick={() => {
                if (isBoardCard) {
                  setShowDeleteConfirm(true);
                } else {
                  // Capture item snapshot for undo before deleting
                  pushAction({
                    type: 'DELETE_ITEM',
                    timestamp: Date.now(),
                    items: [item],
                  });
                  deleteItem(item.id);
                }
              }}
              className="text-destructive focus:bg-destructive focus:text-white"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </ContextMenuItem>
          </>
        )}
      </ContextMenuContent>

      {/* Board Rename Dialog - rendered via portal to avoid canvas zoom affecting size */}
      {isRenaming && createPortal(
        <div 
          className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center"
          onClick={() => setIsRenaming(false)}
        >
          <div 
            className="bg-card rounded-lg p-4 shadow-xl w-80 border"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold mb-3">Rename Board</h3>
            <Input
              value={renameValue}
              onChange={(e) => setRenameValue(e.target.value)}
              placeholder="Board name"
              className="mb-4"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleRenameSubmit();
                if (e.key === 'Escape') setIsRenaming(false);
              }}
            />
            <div className="flex justify-end gap-2">
              <button 
                onClick={() => setIsRenaming(false)}
                className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground"
              >
                Cancel
              </button>
              <button 
                onClick={handleRenameSubmit}
                className="px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
              >
                Rename
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Media Rename Dialog - rendered via portal to avoid canvas zoom affecting size */}
      {isRenamingMedia && createPortal(
        <div 
          className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center"
          onClick={() => setIsRenamingMedia(false)}
        >
          <div 
            className="bg-card rounded-lg p-4 shadow-xl w-80 border"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold mb-3">Rename {item.item_type === 'video' ? 'Video' : 'Image'}</h3>
            <Input
              value={mediaRenameValue}
              onChange={(e) => setMediaRenameValue(e.target.value)}
              placeholder="Enter name"
              className="mb-4"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleMediaRenameSubmit();
                if (e.key === 'Escape') setIsRenamingMedia(false);
              }}
            />
            <div className="flex justify-end gap-2">
              <button 
                onClick={() => setIsRenamingMedia(false)}
                className="px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground"
              >
                Cancel
              </button>
              <button 
                onClick={handleMediaRenameSubmit}
                className="px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
              >
                Rename
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Board Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Board</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{linkedBoardName || 'this board'}"? 
              This will also delete all items inside the board. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteBoardConfirm} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Board Profile Popover */}
      {showBoardProfile && linkedBoardId && (
        <BoardProfilePopover
          boardId={linkedBoardId}
          boardName={linkedBoardName || 'Board'}
          onClose={() => setShowBoardProfile(false)}
          position={boardProfilePos}
        />
      )}
    </ContextMenu>
  );
}
